import { useState, useMemo } from 'react';
import { Card, H2, Btn, CChart, Notice, Pill } from '../ui.jsx';
import { C, fmt, fmtDate, fmtDateTime, fmtSigned, pctSigned, CJSTT, mkScale } from '../lib/format.js';
import { ACCOUNTS, trendSeries, varianceReport, defaultLabel, asOfLabel } from '../lib/snapshots.js';

export default function History({
  snapshots, settings, onSaveCheckup, onOpenSnapshot, onDeleteSnapshot, busy, L,
}) {
  const { isNarrow, g2, ch } = L;

  const [label, setLabel] = useState('');
  const [series, setSeries] = useState('total');   // 'total' | 'accounts'
  const [baseId, setBaseId] = useState('');

  // Chronological history built purely from what was actually recorded.
  const points = useMemo(() => trendSeries(snapshots), [snapshots]);
  const hasHistory = points.length > 0;

  // ── Trend chart ──
  const trendId = useMemo(() => `${series}:${points.length}:${points.map(p => p.id).join('')}`, [series, points]);
  const trendCfg = useMemo(() => {
    const labels = points.map(p => p.asOf);
    const line = (lbl, col, data, width) => ({
      label: lbl, data, borderColor: col, backgroundColor: col + '22',
      fill: series === 'total', tension: .25, borderWidth: width || 2, pointRadius: 3, pointBackgroundColor: col,
    });
    const datasets = series === 'total'
      ? [line('Total investable', C.gold, points.map(p => Math.round(p.accounts.total / 1000)), 3)]
      : ACCOUNTS.filter(a => !a.excluded).map(a =>
        line(a.label, a.colour, points.map(p => Math.round(p.accounts[a.key] / 1000))));
    return {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          tooltip: {
            ...CJSTT,
            callbacks: { label: c => `${c.dataset.label}: £${c.parsed.y}k` },
          },
          legend: { labels: { color: C.text, boxWidth: 12, font: { size: 11 } } },
        },
        scales: mkScale(),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendId]);

  // ── Variance ──
  const base = useMemo(
    () => snapshots.find(s => s.id === baseId) || null,
    [snapshots, baseId]
  );
  const variance = useMemo(
    () => (base ? varianceReport(base, settings) : null),
    [base, settings]
  );

  const growth = hasHistory && points.length > 1
    ? points[points.length - 1].accounts.total - points[0].accounts.total
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Save a checkup ── */}
      <Card>
        <H2 sub="Freezes today's balances and assumptions as a permanent, dated record. Snapshots can never be edited afterwards — that is what makes them trustworthy history.">
          Save a Checkup
        </H2>
        <div style={{ display: 'flex', gap: 10, flexDirection: isNarrow ? 'column' : 'row', alignItems: isNarrow ? 'stretch' : 'center' }}>
          <input
            type="text" value={label} onChange={e => setLabel(e.target.value)}
            placeholder={defaultLabel()}
            style={{ flex: 1 }}
          />
          <Btn tone="primary" disabled={busy} onClick={() => { onSaveCheckup(label); setLabel(''); }}>
            {busy ? 'Saving…' : '📌 Save checkup'}
          </Btn>
        </div>
      </Card>

      {!hasHistory && (
        <Notice tone="warn">
          <strong style={{ color: C.bright }}>No checkups saved yet.</strong> Once you save your first one, this tab will
          start charting your real balances over time and comparing past forecasts against what actually happened. Save one
          now to begin the record.
        </Notice>
      )}

      {hasHistory && (
        <>
          {/* ── Trends ── */}
          <Card>
            <H2 sub="Balances you actually recorded, in £000s. This is history, not projection — no growth assumptions are applied to these lines.">
              Recorded Balances Over Time
            </H2>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <Pill label="Total" active={series === 'total'} onClick={() => setSeries('total')} />
              <Pill label="By account" active={series === 'accounts'} onClick={() => setSeries('accounts')} color={C.teal} />
            </div>
            {points.length === 1 ? (
              <Notice>
                One checkup recorded so far. Trend lines appear once there are at least two — save another at your next
                statement review and the chart will start to build.
              </Notice>
            ) : (
              <CChart cfg={trendCfg} height={ch(320)} id={trendId} />
            )}
            {growth !== null && (
              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3,1fr)', gap: 12 }}>
                {[
                  ['First recorded', points[0].asOf, fmt(points[0].accounts.total), C.steel],
                  ['Latest recorded', points[points.length - 1].asOf, fmt(points[points.length - 1].accounts.total), C.bright],
                  ['Change over that period', points.length + ' checkups', fmtSigned(growth), growth >= 0 ? C.green : C.red],
                ].map(([l, sub, v, col]) => (
                  <div key={l} style={{ background: C.surf, borderRadius: 10, padding: '14px' }}>
                    <div style={{ fontSize: 9, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>{l}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: col, fontFamily: "'DM Mono',monospace", marginTop: 5 }}>{v}</div>
                    <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>{sub}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* ── Forecast vs actual ── */}
          <Card>
            <H2 sub="Pick a past checkup. The app re-runs the forecast it was making back then, carries it forward to where you are now, and compares it against your latest actual balances.">
              Forecast vs Actual
            </H2>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: C.lbl, marginBottom: 6 }}>Compare this past checkup…</div>
              <select
                value={baseId} onChange={e => setBaseId(e.target.value)}
                style={{
                  width: '100%', minHeight: 48, background: '#071420', border: `1px solid ${C.bdr}`,
                  color: C.text, borderRadius: 8, padding: '11px 12px', fontSize: 16,
                  fontFamily: "'DM Sans',system-ui", WebkitAppearance: 'none', appearance: 'none',
                }}
              >
                <option value="">— choose a checkup —</option>
                {snapshots.map(s => (
                  <option key={s.id} value={s.id}>{s.label} · as at {asOfLabel(s.settings)}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
                …against your current working values ({settings.asOfDate || 'today'}).
              </div>
            </div>

            {!variance && (
              <Notice>Choose a checkup above to see how its forecast has held up.</Notice>
            )}

            {variance && variance.monthsElapsed === 0 && (
              <Notice tone="warn">
                That checkup has the same as-of date as your current values, so no time has elapsed between them and there is
                nothing to compare yet. Update the <strong style={{ color: C.bright }}>Simulation start</strong> year/month on
                the Values tab when you record new balances.
              </Notice>
            )}

            {variance && variance.monthsElapsed > 0 && (
              <>
                <div style={{
                  background: variance.totalRow.ahead ? C.green + '15' : C.amb + '15',
                  border: `1px solid ${variance.totalRow.ahead ? C.green : C.amb}44`,
                  borderRadius: 12, padding: '16px 18px', marginBottom: 16,
                }}>
                  <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                    {variance.monthsElapsed} months on from "{base.label}"
                  </div>
                  <div style={{
                    fontSize: isNarrow ? 24 : 30, fontWeight: 700, marginTop: 6,
                    color: variance.totalRow.ahead ? C.green : C.amb, fontFamily: "'DM Mono',monospace",
                  }}>
                    {fmtSigned(variance.totalRow.delta)} <span style={{ fontSize: isNarrow ? 15 : 18 }}>({pctSigned(variance.totalRow.pct)})</span>
                  </div>
                  <div style={{ fontSize: 14, color: C.bright, marginTop: 6, fontWeight: 600 }}>
                    {variance.totalRow.ahead ? '✓ Ahead of plan' : '▾ Behind plan'}
                  </div>
                  <div style={{ fontSize: 12, color: C.dim, marginTop: 6, lineHeight: 1.6 }}>
                    That forecast expected {fmt(variance.totalRow.predicted)} by now. You actually have{' '}
                    {fmt(variance.totalRow.actual)}.
                  </div>
                </div>

                <div className="scroll-x">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 560 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.bdr}` }}>
                        {['Account', 'Forecast said', 'Actually is', 'Gap', '%'].map((h, i) => (
                          <th key={h} style={{
                            padding: '10px 10px', textAlign: i === 0 ? 'left' : 'right', color: C.lbl,
                            fontSize: 10, textTransform: 'uppercase', letterSpacing: .5, fontWeight: 500, whiteSpace: 'nowrap',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {variance.rows.map((r, idx) => (
                        <tr key={r.key} style={{
                          borderBottom: `1px solid ${C.bdr}`,
                          background: idx % 2 === 0 ? C.surf + '55' : 'transparent',
                          opacity: r.excluded ? .6 : 1,
                        }}>
                          <td style={{ padding: '9px 10px', color: r.colour, whiteSpace: 'nowrap' }}>
                            {r.label}{r.excluded && <span style={{ color: C.steel, fontSize: 10 }}> (excl.)</span>}
                          </td>
                          <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.dim }}>{fmt(r.predicted)}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.text }}>{fmt(r.actual)}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: r.ahead ? C.green : C.amb, fontWeight: 600 }}>{fmtSigned(r.delta)}</td>
                          <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: r.ahead ? C.green : C.amb }}>{pctSigned(r.pct)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: `2px solid ${C.bdr}` }}>
                        <td style={{ padding: '11px 10px', color: C.gold, fontWeight: 700 }}>{variance.totalRow.label}</td>
                        <td style={{ padding: '11px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.dim, fontWeight: 700 }}>{fmt(variance.totalRow.predicted)}</td>
                        <td style={{ padding: '11px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: C.bright, fontWeight: 700 }}>{fmt(variance.totalRow.actual)}</td>
                        <td style={{ padding: '11px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: variance.totalRow.ahead ? C.green : C.amb, fontWeight: 700 }}>{fmtSigned(variance.totalRow.delta)}</td>
                        <td style={{ padding: '11px 10px', textAlign: 'right', fontFamily: "'DM Mono',monospace", color: variance.totalRow.ahead ? C.green : C.amb, fontWeight: 700 }}>{pctSigned(variance.totalRow.pct)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p style={{ fontSize: 11, color: C.dim, marginTop: 12, lineHeight: 1.6 }}>
                  A <strong style={{ color: C.green }}>positive gap means ahead of plan</strong> — the real balance beat what
                  that older forecast predicted. Cash ISAs are shown for completeness but, as everywhere else in this app, sit
                  outside the total. {variance.truncated && ' Note: more time has passed than that forecast modelled, so the comparison uses its final month.'}
                </p>
              </>
            )}
          </Card>

          {/* ── Snapshot list ── */}
          <Card>
            <H2 sub="Open any checkup to replay the app exactly as it looked on that date — same balances, same assumptions, same forecast.">
              Saved Checkups ({snapshots.length})
            </H2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {snapshots.map(s => {
                const acc = points.find(p => p.id === s.id)?.accounts;
                return (
                  <div key={s.id} style={{
                    background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: '14px 16px',
                    display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: C.bright }}>{s.label}</div>
                      <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>
                        Balances as at <span style={{ color: C.steel }}>{asOfLabel(s.settings)}</span>
                        {acc && <> · <span style={{ fontFamily: "'DM Mono',monospace", color: C.gold }}>{fmt(acc.total)}</span></>}
                      </div>
                      <div style={{ fontSize: 10, color: C.lbl, marginTop: 2 }}>Saved {fmtDateTime(s.createdAt)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn onClick={() => onOpenSnapshot(s)} style={{ minHeight: 44, padding: '10px 16px', fontSize: 14 }}>Open</Btn>
                      <Btn tone="danger" onClick={() => onDeleteSnapshot(s)} style={{ minHeight: 44, padding: '10px 14px', fontSize: 14 }}>Delete</Btn>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
